import { useCallback, useEffect, useState } from 'react';
import { analyticsUrl, trendWindowHours } from '../utils/platformHealth';

const fetchAnalytics = async (params, signal) => {
  const response = await fetch(
    analyticsUrl(process.env.REACT_APP_AUTH_BACKEND_URL, params),
    { credentials: 'include', signal },
  );
  if (!response.ok) throw new Error(`Status ${response.status}`);
  const body = await response.json();
  return body.latency_analytics;
};

// Loads one window's totals and its day-by-day trend, and drops the answer to
// any request a newer choice has replaced.
export default function usePlatformHealth({ windowHours, kind, groupBy }) {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [rollups, setRollups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    Promise.all([
      fetchAnalytics(
        { window_hours: windowHours, kind, group_by: groupBy },
        signal,
      ),
      fetchAnalytics(
        {
          window_hours: trendWindowHours(windowHours),
          kind,
          group_by: 'day',
        },
        signal,
      ),
    ])
      .then(([nextSummary, nextTrend]) => {
        if (signal.aborted) return;
        setSummary(nextSummary);
        setTrend(nextTrend.groups);
        setRollups({
          behind: nextTrend.rollups_behind,
          through: nextTrend.rolled_up_through,
        });
        setFailed(false);
        setLoading(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setSummary(null);
        setTrend([]);
        setRollups(null);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [windowHours, kind, groupBy, reloads]);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  return { summary, trend, rollups, loading, failed, reload };
}
